import {getLocale} from '@github-ui/client-env'
import type {CopilotModel} from '@github-ui/copilot-chat-types'
import {useCallback, useMemo, useSyncExternalStore} from 'react'

import {getModelPromoExpiry, isModelPromoActive} from './model-picker-helpers'

type ModelPromo = NonNullable<NonNullable<CopilotModel['billing']>['promo']>
export type ActiveModelPromo = ModelPromo & {
  messageBeforeEndDate?: string
  formattedEndDate?: string
  endDateLocale?: string
}

const MAX_TIMEOUT_MS = 2_147_483_647
const NOOP = () => {}
const PROMO_END_DATE_FORMAT_OPTIONS = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
} satisfies Intl.DateTimeFormatOptions
const promoEndDateFormatters = new Map<string, Intl.DateTimeFormat>()
const getServerSnapshot = () => false

/**
 * Re-notifies the store when the promo expires. `setTimeout` saturates past ~24.8 days, so long
 * expiries are reached by re-arming the timer until the remaining time fits in one timeout.
 *
 * This lives outside the hook because the self-scheduling timer keeps mutable state, which the
 * React Compiler cannot reconcile with the `subscribe` callback's declared dependencies.
 */
function watchPromoExpiry(expiry: number, onExpired: () => void): () => void {
  let timeout: ReturnType<typeof setTimeout> | undefined

  const scheduleExpiry = () => {
    const remaining = expiry - Date.now()
    if (remaining <= 0) {
      onExpired()
      return
    }

    timeout = setTimeout(scheduleExpiry, Math.min(remaining, MAX_TIMEOUT_MS))
  }

  scheduleExpiry()
  return () => {
    if (timeout !== undefined) clearTimeout(timeout)
  }
}

function formatPromoEndDate(expiry: number): {formattedEndDate: string; endDateLocale: string} {
  const locale = getLocale()
  let formatter = promoEndDateFormatters.get(locale)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, PROMO_END_DATE_FORMAT_OPTIONS)
    promoEndDateFormatters.set(locale, formatter)
  }
  return {
    formattedEndDate: formatter.format(expiry),
    endDateLocale: formatter.resolvedOptions().locale,
  }
}

export function useActiveModelPromo(promo: ModelPromo | undefined): ActiveModelPromo | undefined {
  const hasEndsAt = promo?.ends_at !== undefined
  const expiry = getModelPromoExpiry(promo?.ends_at)
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!hasEndsAt || expiry === undefined || expiry <= Date.now()) return NOOP

      return watchPromoExpiry(expiry, onStoreChange)
    },
    [expiry, hasEndsAt],
  )
  const getSnapshot = useCallback(() => isModelPromoActive(promo), [promo])
  const isActive = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  return useMemo(() => {
    if (!isActive || !promo) return undefined
    if (!hasEndsAt || expiry === undefined || !promo.message) return promo

    const baseMessage = promo.message.trimEnd()
    const sentenceTerminator = /[.!?]$/.test(baseMessage) ? '' : '.'
    const messageBeforeEndDate = `${baseMessage}${sentenceTerminator}`
    const {formattedEndDate, endDateLocale} = formatPromoEndDate(expiry)
    return {
      ...promo,
      message: `${messageBeforeEndDate} Ends ${formattedEndDate}.`,
      messageBeforeEndDate,
      formattedEndDate,
      endDateLocale,
    }
  }, [expiry, hasEndsAt, isActive, promo])
}
